import React, { Component } from 'react';

import isNaN from 'lodash/isNaN';
import toNumber from 'lodash/toNumber';

import {
  TestCaseBoxStepsActions,
  TestCaseBoxTitle,
} from '@qality/common-components';
import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { LastExecutionBar } from '@qality/common-components/src/molecules/LastExecutionBar';
import { messages } from '@qality/common-messages';
import {
  LastExecution,
  TestCase,
  TestStep,
  TestStepsCollection,
  TestStepsView,
} from '@qality/common-model';
import { TestCaseFullScreenCloseCallbackData } from '@qality/common-model/src/TestCaseFullScreenData';
import {
  StorageOfUniqueServices,
  TestStepService,
} from '@qality/common-service';

import { TestCaseBoxStyled } from './TestCaseBox.styled';
import TestSteps from './TestSteps';

type Props = {
  testCase: TestCase;
  onUpdateTestCase: (
    id: number | string,
    testCase: TestCase,
    updateOnlyState?: boolean
  ) => void;
  canEdit: boolean;
  testCaseIndex: number;
  deleteTestCase: (testCase: TestCase) => void;
  view: TestStepsView;
  expanded: boolean;
  expandOneTestCase: (testCaseIndex: number, value: boolean) => void;
  issueKey: string;
  syncDataFromFullScreen: (data: TestCaseFullScreenCloseCallbackData) => void;
  lastExecution: LastExecution;
};

type State = {
  expanded: boolean;
  testCaseIssueKey: string;
  testCaseIssueId?: number | string;
  isHovered: boolean;
  lastExecution: LastExecution;
};

export default class TestCaseBox extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      expanded: this.props.expanded,
      testCaseIssueKey: '',
      testCaseIssueId: undefined,
      isHovered: false,
      lastExecution: null,
    };
  }

  async componentDidMount(): Promise<void> {
    if (this.props.testCase) {
      const { key, id } = await this.props.testCase.issue;
      this.setState({ testCaseIssueKey: key, testCaseIssueId: id });
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.expanded !== this.props.expanded) {
      this.setState({ expanded: this.props.expanded });
    }
  }

  onToggleVisibility = (value: boolean): void => {
    this.setState({
      expanded: value,
    });
    this.props.expandOneTestCase(this.props.testCaseIndex, value);
  };

  saveTestCaseName = async (name: string): Promise<boolean | void> => {
    try {
      const { testCase } = this.props;
      return await this.props.onUpdateTestCase(testCase.id, {
        ...testCase,
        name,
      });
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBox.saveTestCaseName.message']
      );
      return true;
    }
  };

  addTestStep = async (): Promise<boolean | void> => {
    try {
      const { testCase } = this.props;
      const testCaseId = toNumber((await this.props.testCase.issue).id);
      if (!isNaN(testCaseId)) {
        const testSteps = new TestStepsCollection(
          await Promise.all(
            testCase.testSteps.testSteps.map(async (testStep) => {
              testStep.testCaseId = testCaseId;
              return TestStepService.createNewTestStepIfNeeded(
                testStep,
                this.state.testCaseIssueId
              );
            })
          )
        );

        testSteps.testSteps.push(new TestStep(testCaseId));
        this.onToggleVisibility(true);
        return await this.props.onUpdateTestCase(
          testCase.id,
          { ...testCase, testSteps },
          true
        );
      }
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBox.saveTestStep.message']
      );
      return true;
    }
  };

  onDeleteTestCase = (): void => {
    this.props.deleteTestCase(this.props.testCase);
  };

  updateTestCase = async (testSteps: TestStepsCollection): Promise<void> => {
    return await this.props.onUpdateTestCase(
      this.props.testCase.id,
      { ...this.props.testCase, testSteps },
      true
    );
  };

  render(): JSX.Element {
    return (
      <TestCaseBoxStyled expanded={this.state.expanded}>
        <div
          onMouseEnter={() => this.setState({ isHovered: true })}
          onMouseLeave={() => this.setState({ isHovered: false })}
        >
          <TestCaseBoxTitle
            canEdit={this.props.canEdit}
            onToggleVisibility={this.onToggleVisibility}
            name={this.props.testCase.name}
            saveTestCaseName={this.saveTestCaseName}
            deleteTestCase={this.onDeleteTestCase}
            addTestStep={this.addTestStep}
            expanded={this.state.expanded}
            testCase={this.props.testCase}
            issueKey={this.props.issueKey}
            isHovered={this.state.isHovered}
            isIssueView={true}
            syncDataFromFullScreen={this.props.syncDataFromFullScreen}
            lastExecution={this.props.lastExecution}
          />
        </div>
        {this.state.expanded && (
          <PremiumFeatureGuard
            premium={
              <LastExecutionBar
                testCase={this.props.testCase}
                issueKey={this.props.issueKey}
                lastExecution={this.props.lastExecution}
              />
            }
          />
        )}
        <TestSteps
          canEdit={this.props.canEdit}
          testStepsCollection={this.props.testCase.testSteps}
          expanded={this.state.expanded}
          view={this.props.view}
          onUpdateTestSteps={this.updateTestCase}
          testCaseIssueKey={this.state.testCaseIssueKey}
          testCaseIssueId={this.state.testCaseIssueId}
          droppableId={String(this.props.testCase.id)}
        />

        {this.state.expanded && (
          <TestCaseBoxStepsActions
            canEdit={this.props.canEdit}
            addTestStep={this.addTestStep}
          />
        )}
      </TestCaseBoxStyled>
    );
  }
}
